import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { KARTE_DESIGNER_ROWS, colorVarOf, onDesignerColor } from './shared.js';
import type { KarteDesignerId } from './shared.js';

/**
 * デザイナー個人の小さなシンボル icon (24×24)。
 * @graph-connects none
 */
const DESIGNER_ICON_PATHS: Record<KarteDesignerId, ReactNode> = {
  // seven = スポーツカー: マツダ RX-7 (FD3S) の側面を参照した輪郭。ロングノーズ / 寝かせた
  // フロントガラス / ファストバックだけを残し、ホイールは下向きのふくらみとして輪郭に含める
  // (他の designer icon と同じ、面で見せる単色シルエットに揃えるためディテールは持たせない)。
  // 実車の縦横比のままだと 24x24 で細く小さく見えるため、全長を詰めて縦に伸ばし、
  // 他の icon と同じ画面占有率にしてある。中抜きはサイドウィンドウ 1 箇所のみ。
  seven: (
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.72 12.47C2.21 12.25 3.63 11.49 4.66 11.16C5.69 10.83 7.03 10.66 7.9 10.47C8.76 10.29 9.1 10.7 9.84 10.03C10.58 9.37 11.6 7.13 12.32 6.48C13.04 5.84 13.24 5.94 14.16 6.16C15.08 6.38 16.64 7.2 17.83 7.79C19.02 8.39 20.49 9.25 21.29 9.73C22.09 10.2 22.38 10.26 22.63 10.64C22.88 11.01 22.95 11.2 22.8 11.97C22.65 12.73 21.9 14.69 21.72 15.23L20.69 15.23A2.6 2.6 0 0 1 15.49 15.23L8.51 15.23A2.6 2.6 0 0 1 3.31 15.23L2.28 15.23C2.11 15.06 1.46 14.53 1.29 14.16C1.12 13.8 1.19 13.34 1.26 13.06C1.34 12.77 1.64 12.57 1.72 12.47ZM11.35 9.77C11.62 9.37 12.36 7.77 12.97 7.37C13.58 6.97 14.27 7.16 15.02 7.37C15.78 7.58 17.08 8.43 17.49 8.64Z"
    />
  ),
  pippin: (
    <path d="M8 5a1 1 0 011-1c.6 0 1 .5 1 1v3.4a8 8 0 014 0V5a1 1 0 011-1c.6 0 1 .5 1 1v4c1.5.7 2.5 2.2 2.5 3.9 0 2.7-3 4.6-6.5 4.6S5.5 15.6 5.5 13c0-1.7 1-3.2 2.5-3.9V5z" />
  ),
  // myon = セキセイインコ (budgerigar): abejo のロボットと同じく「頭だけ」の構成。
  // 輪郭は真円ではなく、上がすぼまり下 (頬) が張った洋ナシ型にしてインコの頭らしく見せる。
  // 頭頂にちょこんとした冠羽、豆粒の目 2 つと小さなくちばしは abejo と同じ中抜き表現。
  // くちばしを上下に割る案は 30px で割れ目が潰れて点 2 つに見えるため、割らない小さな
  // 三角にしている。
  myon: (
    <g>
      <path d="M10.2 8.2C9.2 6.8 9.6 5.1 10.9 4.4C10.6 5.8 11.3 6.9 12.4 7.4Z" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 7C15.1 7 18 9.2 19 12.9C19.8 16.4 16.1 19.2 12 19.2C7.9 19.2 4.2 16.4 5 12.9C6 9.2 8.9 7 12 7ZM7.75 12.4A1.25 1.25 0 1 0 10.25 12.4A1.25 1.25 0 1 0 7.75 12.4ZM13.75 12.4A1.25 1.25 0 1 0 16.25 12.4A1.25 1.25 0 1 0 13.75 12.4ZM10.5 14.7Q12 14.25 13.5 14.7Q12.8 16.6 12 16.7Q11.2 16.6 10.5 14.7Z"
      />
    </g>
  ),
  // abejo = ロボットの頭 (🤖): アンテナ + 角丸ヘッド + 目2つ・口 (中抜き) + 両耳
  abejo: (
    <g>
      <path d="M12 1.1a1 1 0 100 2 1 1 0 000-2zM11.6 3h.8v2.5h-.8z" />
      <path d="M3.4 10.6h1.9v2.8H3.4zM18.7 10.6h1.9v2.8h-1.9z" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 5h8a3 3 0 013 3v7a3 3 0 01-3 3H8a3 3 0 01-3-3V8a3 3 0 013-3zM9.7 9.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM14.3 9.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM9 14.5h6a1 1 0 010 2H9a1 1 0 010-2z"
      />
    </g>
  ),
  guccy: (
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.5 4l-1.5 2.5H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2v-9a2 2 0 00-2-2h-3l-1.5-2.5h-7zm3.5 3.5a4.5 4.5 0 100 9 4.5 4.5 0 000-9zm0 2a2.5 2.5 0 100 5 2.5 2.5 0 000-5z"
    />
  ),
  seddy: (
    <g>
      <circle cx="12" cy="6.5" r="2.4" />
      <circle cx="6.5" cy="10.2" r="2.4" />
      <circle cx="17.5" cy="10.2" r="2.4" />
      <circle cx="8.5" cy="15.5" r="2.4" />
      <circle cx="15.5" cy="15.5" r="2.4" />
      <circle cx="12" cy="11.8" r="1.7" />
    </g>
  ),
  yan: (
    <path d="M3 12.6c0-2 2.5-3.3 5-2.4 1.3.5 2.5 1.2 4 1.2s2.7-.7 4-1.2c2.5-1 5 .4 5 2.4 0 1.6-2 3.2-4.2 3.2-1.6 0-2.7-.8-3.2-1.3l-1.6-.4-1.6.4c-.5.5-1.6 1.3-3.2 1.3C5 15.8 3 14.2 3 12.6z" />
  ),
  // okupi = 毛筆 (calligraphy brush): 右上へ伸びる柄 + 左下の穂先
  okupi: (
    <path d="M7 14c-1.66 0-3 1.34-3 3 0 1.31-1.16 2-2 2 .92 1.22 2.49 2 4 2 2.21 0 4-1.79 4-4 0-1.66-1.34-3-3-3zm13.71-9.37l-1.34-1.34a.9959.9959 0 00-1.41 0L9 12.25 11.75 15l8.96-8.96a.9959.9959 0 000-1.41z" />
  ),
  // sachi = 眼鏡 (glasses): 2 枚のレンズ (中抜き) + ブリッジ + テンプル
  sachi: (
    <g>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.8 9.3a3.2 3.2 0 100 6.4 3.2 3.2 0 000-6.4zm0 1.5a1.7 1.7 0 100 3.4 1.7 1.7 0 000-3.4z"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.2 9.3a3.2 3.2 0 100 6.4 3.2 3.2 0 000-6.4zm0 1.5a1.7 1.7 0 100 3.4 1.7 1.7 0 000-3.4z"
      />
      <path d="M10 11.9h4v1.3h-4z" />
      <path d="M2 8.7l2.6 1.5-.6 1L1.4 9.7z" />
      <path d="M22 8.7l-2.6 1.5.6 1 2.6-1.5z" />
    </g>
  ),
};

/** @graph-connects none */
function DesignerIcon({ id }: { id: KarteDesignerId }) {
  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ display: 'block' }}
      aria-hidden="true"
    >
      {DESIGNER_ICON_PATHS[id]}
    </svg>
  );
}

/** @graph-connects none */
const CARD_W = 64;
/** @graph-connects none */
const CARD_H = 84;
/** @graph-connects none */
const STEP = 38;
interface DesignerKarteRowProps {
  onOpen: (id: KarteDesignerId) => void;
}

/**
 * karte を持つデザイナーの karte カードを表示段 (行) 別に重ねて表示する。クリックで KarteModal を開く。
 * KARTE_DESIGNER_ROWS の各行が 1 段: 1 段目 = 従来メンバー、2 段目 = 後から追加した okupi / sachi。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI ページ donut 下の karte 行 (karte 対象デザイナーのシンボルカードを段別に重ね表示)。各カード click で KarteModal を開く
 * @graph-connects none
 */
export function DesignerKarteRow({ onOpen }: DesignerKarteRowProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] px-0.5">
        {t('idtKpi.karte.sectionTitle')}
      </span>
      {KARTE_DESIGNER_ROWS.map((row, rowIdx) => (
        <div
          key={row[0]}
          className="relative mx-auto"
          style={{
            width: (row.length - 1) * STEP + CARD_W,
            // 1 段目は hover バブル用に上部を広く取り、2 段目以降は詰めて配置する。
            height: CARD_H + (rowIdx === 0 ? 72 : 20),
          }}
        >
          {row.map((id, idx) => (
            <DesignerKarteCard
              key={id}
              id={id}
              left={idx * STEP}
              zIndex={row.length - idx}
              onClick={() => {
                onOpen(id);
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** @graph-connects none */
function DesignerKarteCard({
  id,
  left,
  zIndex,
  onClick,
}: {
  id: KarteDesignerId;
  left: number;
  zIndex: number;
  onClick: () => void;
}) {
  const colorVar = colorVarOf(id);
  // 気泡 / アイコンに白を載せるとコントラスト不足になる淡色の個人色は深色にする。
  const onColorText = onDesignerColor(id);
  return (
    <button
      type="button"
      onClick={onClick}
      className="karte-card group absolute bottom-2 cursor-pointer focus:outline-none"
      style={{
        left: `${String(left)}px`,
        width: `${String(CARD_W)}px`,
        height: `${String(CARD_H)}px`,
        zIndex,
        background: `color-mix(in srgb, var(${colorVar}) 50%, var(--color-bg-surface))`,
        ['--karte-color' as string]: `var(${colorVar})`,
      }}
      aria-label={`${id} karte`}
    >
      <div
        className="absolute inset-0 flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity duration-[400ms]"
        style={{ color: onColorText }}
        aria-hidden="true"
      >
        <DesignerIcon id={id} />
      </div>
      <div
        className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2
                   opacity-0 scale-75 translate-y-1
                   group-hover:opacity-100 group-hover:scale-100 group-hover:translate-y-0
                   transition-all duration-[260ms] ease-out delay-[80ms]
                   pointer-events-none origin-bottom"
      >
        <div
          className="px-2 py-1 rounded-md text-xs font-semibold shadow-md whitespace-nowrap"
          style={{
            background: `var(${colorVar})`,
            color: onColorText,
          }}
        >
          {id}
        </div>
        <div
          className="absolute left-1/2 -translate-x-1/2 -bottom-[5px] w-0 h-0"
          style={{
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: `5px solid var(${colorVar})`,
          }}
        />
      </div>
    </button>
  );
}
